import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { Observable, Subscribable } from 'rxjs'
import { NotificationService } from './notification.service'
import { PATH } from '../app.api'
import { Demand, NotifyRequester } from '../models/Demand'
import { DataCsv } from 'src/app/models/DataCsv'
import { Address } from '../models/Address'
import * as FileSaver from 'file-saver'
import { AppraisalCommunication, AppraisalEvaluationArboreal } from '../models/Appraisal'

@Injectable()
export class DemandService {
	constructor(
		private http: HttpClient,
		private readonly notificationService: NotificationService
	) {}

	public sendSolicitation(dataCsv: DataCsv): Observable<any> {
		const options: HttpHeaders = this.setHeader()

		return this.http.post<any>(`${PATH}/demand`, JSON.stringify(dataCsv), { headers: options }).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public sendManySolicitation(dataCsv: Array<DataCsv>): Observable<any> {
		const options: HttpHeaders = this.setHeader()

		return this.http.post<any>(`${PATH}/demand/all`, JSON.stringify(dataCsv), { headers: options }).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public getDemandsHistory(companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/history/company/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public updateLocationPinMapHistory(demandId: string, location: Address): Observable<Demand> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/demand/maps/updatePin/${demandId}`, JSON.stringify(location), {
				headers: options
			})
			.pipe(
				map(result => {
					return result
				})
			)
	}

	public updateRequesterData(demandId: string, data): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/appraisal/demand/updateRequester/${demandId}`, JSON.stringify(data), {
				headers: options
			})
			.pipe(
				map(result => {
					return result.message
				})
			)
	}

	public getClickTree(demandId: string, companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/getClick/${companyId}/${demandId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getCommunications(companyId: string, appraisalIds: string, demandId?: string): Observable<Array<AppraisalCommunication>> {
		const options: any = this.setHeader()

		let params = new HttpParams()

		params = params.append('ids', appraisalIds)

		if (demandId) {
			params = params.append('demandId', demandId)
		}

		return this.http.get<any>(`${PATH}/demand/notify/${companyId}`, {
			params,
			headers: options
		}).pipe(
			map(result => {
				return result.content
			})
		)
	}

	public assignCommunication(appraisalsIds: Array<string>): Observable<string> {
		const options: any = this.setHeader()

		return this.http.patch<any>(`${PATH}/demand/notify/assign/${appraisalsIds}`, {
			headers: options
		}).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public unAssignCommunication(appraisalIds: Array<string>): Observable<string> {
		const options: any = this.setHeader()

		return this.http.patch<any>(`${PATH}/demand/notify/unassign/${appraisalIds}`, {
			headers: options
		}).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public notifyRequester(companyId: string, body: NotifyRequester): Observable<string> {
		return this.http.patch<any>(`${PATH}/demand/notify/${companyId}`, body).pipe(
			map(result => {
				return result.message
			})
		)
	}

	public getDemandsWihoutScheduling(companyId: string): Observable<Demand> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/withoutScheduling/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getEvaluationReportArboreal(
		appraisalIds: string,
		demandId?: string
	): Observable<Array<AppraisalEvaluationArboreal>> {
		const options: any = this.setHeader()

		let params = new HttpParams()

		params = params.append('ids', appraisalIds)

		if (demandId) {
			params = params.append('demandId', demandId)
		}

		return this.http
			.get<any>(`${PATH}/appraisal/evaluationReportArboreal`,
			{
				params,
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public deleteAppraisal(apraisalId: string): Observable<Demand> {
		const options: any = this.setHeader()

		return this.http
			.delete<any>(`${PATH}/appraisal/inactivateAppraisal/${apraisalId}?`, {
				headers: options
			})
	}

	public reactivateAppraisal(apraisalId: string): Observable<Demand> {
		const options: any = this.setHeader()

		return this.http
			.put<any>(`${PATH}/appraisal/reactivateAppraisal/${apraisalId}?`, {
				headers: options
			})
	}

	public saveReport(report: any, appraisalId: string): Observable<Demand> {
		const options: any = this.setHeader()

		return this.http.patch<any>(
			`${PATH}/appraisal/report/${appraisalId}`,
			JSON.stringify(report),
			{ headers: options }
		)
	}

	public updateMapCache(companyId: string, userEmail: string): Observable<Array<any>> {
		const options: any = this.setHeader()

		return this.http
			.patch<any>(`${PATH}/demand/${companyId}/${userEmail}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getQuestionStateTree(companyId: string): Observable<any> {
		const options: any = this.setHeader()

		return this.http
			.get<any>(`${PATH}/demand/questions/${companyId}`, {
				headers: options
			})
			.pipe(
				map(result => {
					return result.content
				})
			)
	}

	public getExportAppraisalsHistory(companyId: string, dateStart?: number, dateEnd?: number): Observable<any> {
		const options: any = this.setHeader()

		const params = new HttpParams()
						.set('companyId', companyId)
			.set('dateStart', String(dateStart))
			.set('dateEnd', String(dateEnd))

		return this.http
			.get<any>(`${PATH}/demand/history/all`, {
				headers: options,
				params
			})
			.pipe(
				map(result => {
					const stringBase64 = result.content

				 if (stringBase64 == undefined) {
					return this.notificationService.notify('Não há dados a serem exportados', 420)
				}
				// Decodificar a string Base64 para um array de bytes
				 const byteCharacters = atob(stringBase64)
				 const byteNumbers = new Array(byteCharacters.length)

				 for (let i = 0; i < byteCharacters.length; i++) {
				  byteNumbers[i] = byteCharacters.charCodeAt(i)
				}

				 const byteArray = new Uint8Array(byteNumbers)

				 const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

				// Realizar o download do arquivo automaticamente
				 FileSaver.saveAs(blob, `Relatório_de_histórico_de_avaliação_export_${new Date().getTime()}.xlsx`)
				})
			)
	}

	private setHeader(): any {
		const headers: any = new HttpHeaders({
			'Content-Type': 'application/json'
		})
		const options: any = headers

		return options
	}

	public attachDemandInAppraisal(appraisalIds: Array<string>, demandId: string): Observable<string> {
		const options: HttpHeaders = this.setHeader()

		return this.http.patch<any>(`${PATH}/demand/attach/${demandId}/${appraisalIds}`, { headers: options })
			.pipe(
				map(result => {
					return result.message
				})
			)
	}

	public unattachDemandInAppraisal(appraisalId: string): Observable<string> {
		const options: HttpHeaders = this.setHeader()

		return this.http.patch<any>(`${PATH}/demand/unattach/${appraisalId}`, { headers: options })
			.pipe(
				map(result => {
					return result.message
				})
			)
	}

}
